import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, LayoutList, Settings2, LogOut,
  Bell, Crosshair, Inbox, UserCog, TrendingUp,
} from "lucide-react";

const LEAD_HUNTER_URL = "https://ai.studio/apps/8a2535e8-9f39-4b8a-a8fc-8c61528686ad?fullscreenApplet=true";

function NavTooltip({ label, children }) {
  return (
    <div className="relative group/tip">
      {children}
      <span className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-3 px-2.5 py-1.5 bg-[#1A1A1A] border border-white/10 text-white text-xs font-semibold rounded-lg whitespace-nowrap shadow-xl opacity-0 translate-x-1 group-hover/tip:opacity-100 group-hover/tip:translate-x-0 transition-all duration-150 z-[60]">
        {label}
      </span>
    </div>
  );
}

const linkClsWithBg = ({ isActive }) =>
  `flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-150 ${
    isActive
      ? "text-[#FFC107] bg-[#FFC107]/[0.12]"
      : "text-gray-500 hover:text-white hover:bg-white/[0.08]"
  }`;

export function DesktopSidebar({ profile, notifCount, onToggleNotif, onLogout, notifBtnRef }) {
  const navigate = useNavigate();
  const userInitial = profile?.first_name?.[0]?.toUpperCase() || profile?.email?.[0]?.toUpperCase() || "A";

  return (
    <aside
      className="hidden md:flex fixed left-0 top-0 h-screen w-[72px] flex-col items-center py-5 z-30"
      style={{ background: "#0a0a0a", borderRight: "1px solid rgba(255,255,255,0.06)" }}
    >
      <div className="mb-6">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden shadow-xl" style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.10)" }}>
          <img src="/logo.png" alt="Sitios YA" className="w-10 h-10 object-contain" />
        </div>
      </div>

      <div className="w-8 h-px mb-4" style={{ background: "rgba(255,255,255,0.08)" }} />

      <nav className="flex flex-col gap-1 flex-1">
        <NavTooltip label="Inicio"><NavLink to="/home" data-testid="nav-home" className={linkClsWithBg}><LayoutDashboard size={19} /></NavLink></NavTooltip>
        <NavTooltip label="Bandeja de entrada"><NavLink to="/inbox" data-testid="nav-inbox" className={linkClsWithBg}><Inbox size={19} /></NavLink></NavTooltip>
        <NavTooltip label="Pipeline / Leads"><NavLink to="/pipeline" data-testid="nav-pipeline" className={linkClsWithBg}><LayoutList size={19} /></NavLink></NavTooltip>
        <NavTooltip label="Lead Hunter">
          <a
            href={LEAD_HUNTER_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-lead-hunter"
            className="flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-150 text-gray-500 hover:text-white hover:bg-white/[0.08]"
          >
            <Crosshair size={19} />
          </a>
        </NavTooltip>
        <NavTooltip label="Cuentas"><NavLink to="/accounts" data-testid="nav-accounts" className={linkClsWithBg}><UserCog size={19} /></NavLink></NavTooltip>
        <NavTooltip label="Finanzas"><NavLink to="/finance" data-testid="nav-finance" className={linkClsWithBg}><TrendingUp size={19} /></NavLink></NavTooltip>
      </nav>

      <div className="w-8 h-px mb-3" style={{ background: "rgba(255,255,255,0.08)" }} />

      <NavTooltip label="Configuración">
        <NavLink to="/settings" data-testid="nav-settings" className={linkClsWithBg}>
          <Settings2 size={19} />
        </NavLink>
      </NavTooltip>

      <NavTooltip label="Cerrar sesión">
        <button
          data-testid="logout-btn"
          onClick={onLogout}
          className="flex items-center justify-center w-10 h-10 rounded-xl text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition-all duration-150 mt-1"
        >
          <LogOut size={19} />
        </button>
      </NavTooltip>

      <div className="w-8 h-px my-3" style={{ background: "rgba(255,255,255,0.08)" }} />

      <NavTooltip label="Mi perfil">
        <button
          data-testid="nav-profile"
          onClick={() => navigate("/profile")}
          className="flex items-center justify-center w-12 h-12 rounded-xl overflow-hidden border-2 border-white/10 hover:border-[#ffc700]/40 transition-all duration-150"
        >
          {profile?.avatar
            ? <img src={profile.avatar} alt="avatar" className="w-full h-full object-cover" />
            : <span className="text-white text-base font-black bg-gradient-to-br from-[#ffc700] to-[#ff8c00] w-full h-full flex items-center justify-center">{userInitial}</span>
          }
        </button>
      </NavTooltip>

      <NavTooltip label={`${notifCount} notificaciones`}>
        <button
          ref={notifBtnRef}
          data-testid="notif-bubble"
          onClick={onToggleNotif}
          className="relative mt-2 flex items-center justify-center w-12 h-12 rounded-xl bg-[#1a1a1a] hover:bg-[#242424] border border-white/10 transition-all duration-150"
        >
          <Bell size={20} className="text-gray-400" />
          {notifCount > 0 && (
            <span
              data-testid="notif-badge"
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[9px] font-black rounded-full flex items-center justify-center px-1 border-2 border-[#0D0D0D]"
            >
              {notifCount > 99 ? "99+" : notifCount}
            </span>
          )}
        </button>
      </NavTooltip>
    </aside>
  );
}
